import { useCallback, useMemo, useState } from "react";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Grid from "components/primitives/Grid";
import Paper from "components/mui5/Paper";
import Typography from "@mui/material/Typography";
import useMetadataActions from "providers/MetadataProvider/useMetadataActions";
import MetadataHelpers from "helpers/MetadataHelpers";
import AnalyticsIcon from "components/Icons/AnalyticsIcon";
import { DateTime, Measurement } from "types";
import ActivityCardTable from "./ActivityCardTable";
import BroadDownArrow from "components/Icons/BroadDownArrow";
import BroadUpArrow from "components/Icons/BroadUpArrow";
import { Link as RouterLink } from "react-router-dom";
import ClickHelpers from "helpers/ClickHelpers";
import moment from "moment";
import MeasureHelpers from "features/Charting/helpers/MeasureHelpers";
import * as Components from "./ActivityCard.components";
import { useTranslation } from "react-i18next";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import TooltipHelper from "components/TooltipHelper";

interface ActivityCardProps {
  date: DateTime;
  athleteId: string;
  exerciseId: string;
  measurements: Measurement[];
}

function ActivityCard(props: ActivityCardProps) {
  const [open, setOpen] = useState(false);
  const { exerciseId, athleteId, measurements, date } = props;
  const metadataActions = useMetadataActions();
  const { metadata, name, category } = useMemo(() => {
    const metadata = metadataActions.getById(exerciseId);
    return {
      metadata,
      name: MetadataHelpers.getMetadataName(metadata),
      category: MetadataHelpers.getMetadataCategory(metadata),
    };
  }, [metadataActions, exerciseId]);
  const { t } = useTranslation();

  const toggleExpand = useCallback(() => {
    setOpen((prev) => !prev);
  }, [setOpen]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      // Only respond to keys originating on the card itself, so activating the nested
      // chart link (Enter/Space) doesn't also toggle the card.
      if (event.target !== event.currentTarget) return;

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        toggleExpand();
      }
    },
    [toggleExpand]
  );

  const cardProps = useMemo(
    () => ({
      "aria-label": t("components.named_activity_card_aria_label", {
        name: name || exerciseId,
      }),
      role: Boolean(metadata) ? "button" : undefined,
      tabIndex: Boolean(metadata) ? 0 : undefined,
      onClick: Boolean(metadata) ? toggleExpand : undefined,
      onKeyDown: Boolean(metadata) ? handleKeyDown : undefined,
      sx: {
        borderRadius: 1,
        cursor: Boolean(metadata) ? "pointer" : undefined,
        mb: 1,
      },
    }),
    [metadata, name, exerciseId, t, toggleExpand, handleKeyDown]
  );

  const to = useMemo(() => {
    return MeasureHelpers.getAthleteProfileChartUrl(athleteId, {
      metadataId: exerciseId,
      interval: "custom",
      start: moment(date).startOf("day").toISOString(),
      end: moment().endOf("day").toISOString(),
    });
  }, [athleteId, exerciseId, date]);

  return (
    <Paper {...cardProps}>
      <Box
        sx={{
          pl: 2,
          pr: 2,
          pt: 1.5,
          pb: 2.1,
          position: "relative",
        }}
      >
        <Grid container sx={{ alignItems: "center", flexWrap: "nowrap" }}>
          <Grid size="grow" minWidth={0}>
            {category && (
              <Typography noWrap title={category}>
                <Box component="span" sx={{ color: "grey.800", fontSize: 12 }}>
                  {category}
                </Box>
              </Typography>
            )}
            <Typography variant="h4" noWrap title={name || exerciseId}>
              {name || exerciseId}
            </Typography>
          </Grid>
          <Grid>
            <Box sx={{ color: "primary.main" }}>
              <ThemeProvider2024>
                <TooltipHelper title={t("global.chart_data_label")}>
                  <IconButton
                    component={RouterLink}
                    aria-label={t("athlete_profile.exercise_view_chart_aria_label", {
                      exercise: name || exerciseId,
                    })}
                    size="small"
                    color="inherit"
                    onClick={ClickHelpers.stopPropagation}
                    to={to}
                  >
                    <AnalyticsIcon />
                  </IconButton>
                </TooltipHelper>
              </ThemeProvider2024>
            </Box>
          </Grid>
        </Grid>
        {metadata && (
          <>
            <Collapse in={open} unmountOnExit={true}>
              <ActivityCardTable measurements={measurements} metadata={metadata} />
            </Collapse>
            <Components.ExpandIconDiv>
              {open ? (
                <BroadUpArrow fontSize="small" color="disabled" />
              ) : (
                <BroadDownArrow fontSize="small" color="disabled" />
              )}
            </Components.ExpandIconDiv>
          </>
        )}
      </Box>
    </Paper>
  );
}

export default ActivityCard;
